<div class="relative px-2 py-3">
  @if (loading && !metrics) {
    <div class="flex h-64 items-center justify-center">
      <div class="animate-spin h-6 w-6 rounded-full border-2 border-brand-500 border-t-transparent"></div>
    </div>
  }

  <!-- Header + filter bar -->
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div class="flex items-center gap-2">
      <span class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
        <lucide-icon [name]="LayoutDashboard" class="h-5 w-5"></lucide-icon>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white/90">Sales Dashboard</h2>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          Contact-centric pipeline to Confirmed.
          {{ isManagement ? 'Every venue in view.' : 'Scoped to your venues.' }}
        </p>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <!-- Venue multi-select -->
      <div class="relative">
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white/80"
          (click)="toggleVenueFilterDropdown()"
        >
          <lucide-icon [name]="Filter" class="h-4 w-4"></lucide-icon>
          <span>{{ venueFilterLabel() }}</span>
          <lucide-icon [name]="ChevronDown" class="h-4 w-4"></lucide-icon>
        </button>
        @if (venueFilterOpen) {
          <div class="absolute right-0 z-30 mt-2 w-64 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <div class="flex items-center justify-between px-2 py-1">
              <span class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Filter by venue</span>
              <button type="button" class="text-xs text-brand-500 hover:underline disabled:text-gray-400" [disabled]="selectedVenueIds.size === 0" (click)="clearVenues()">Clear</button>
            </div>
            <div class="max-h-64 overflow-y-auto">
              @for (venue of venueOptions; track venue.id) {
                <label class="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-white/80 dark:hover:bg-white/5">
                  <input type="checkbox" class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" [checked]="isVenueSelected(venue.id)" (change)="toggleVenue(venue.id)" />
                  <span>{{ venue.name }}</span>
                </label>
              } @empty {
                <div class="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">No venues available.</div>
              }
            </div>
          </div>
        }
      </div>

      <!-- Salesperson multi-select -->
      <div class="relative">
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white/80"
          (click)="toggleStaffFilterDropdown()"
        >
          <lucide-icon [name]="Users" class="h-4 w-4"></lucide-icon>
          <span>{{ staffFilterLabel() }}</span>
          <lucide-icon [name]="ChevronDown" class="h-4 w-4"></lucide-icon>
        </button>
        @if (staffFilterOpen) {
          <div class="absolute right-0 z-30 mt-2 w-64 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <div class="flex items-center justify-between px-2 py-1">
              <span class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Filter by salesperson</span>
              <button type="button" class="text-xs text-brand-500 hover:underline disabled:text-gray-400" [disabled]="selectedStaffIds.size === 0" (click)="clearStaff()">Clear</button>
            </div>
            <div class="max-h-64 overflow-y-auto">
              @for (person of staffOptions; track person.id) {
                <label class="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-white/80 dark:hover:bg-white/5">
                  <input type="checkbox" class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" [checked]="isStaffSelected(person.id)" (change)="toggleStaff(person.id)" />
                  <span>{{ person.name }}</span>
                </label>
              } @empty {
                <div class="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">No salespeople available.</div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  </div>

  <!-- Date range presets + custom -->
  <div class="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-3 dark:border-gray-800">
    <div class="flex flex-wrap gap-1.5" role="group" aria-label="Date range preset">
      @for (preset of presets; track preset.key) {
        <button
          type="button"
          class="rounded-full border px-3 py-1 text-xs font-medium transition-colors"
          [ngClass]="activePreset === preset.key
            ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-500/40 dark:bg-brand-500/15 dark:text-brand-300'
            : 'border-gray-300 bg-white text-gray-600 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'"
          (click)="selectPreset(preset.key)"
        >
          {{ preset.label }}
        </button>
      }
    </div>

    @if (activePreset === 'custom') {
      <div class="flex items-center gap-2 text-sm">
        <input type="date" class="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white/80" [(ngModel)]="customFrom" (change)="applyCustomRange()" />
        <span class="text-gray-400">→</span>
        <input type="date" class="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white/80" [(ngModel)]="customTo" (change)="applyCustomRange()" />
      </div>
    }

    <div class="ml-auto flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
      <label class="flex items-center gap-1.5">
        <span class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">High value ≥ £</span>
        <input type="number" min="0" step="500" class="w-24 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white/80" [(ngModel)]="highValueThreshold" (change)="applyThresholds()" />
      </label>
      <label class="flex items-center gap-1.5">
        <span class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Cold after (days)</span>
        <input type="number" min="1" step="1" class="w-16 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white/80" [(ngModel)]="goingColdDays" (change)="applyThresholds()" />
      </label>
    </div>
  </div>

  @if (metrics) {
    <!-- Top-line metric cards. Each carries its own colour identity plus a
         period-over-period trend chip (▲/▼ vs the previous equivalent period). -->
    <div class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <!-- Volume -->
      <button
        type="button"
        class="group relative overflow-hidden rounded-2xl border p-4 text-left transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        [ngClass]="isActiveDrill('volume')
          ? 'border-brand-400 bg-brand-50 ring-2 ring-brand-300 dark:border-brand-500/60 dark:bg-brand-500/15 dark:ring-brand-500/40'
          : 'border-brand-200/70 bg-gradient-to-br from-brand-50 to-white hover:border-brand-300 dark:border-brand-500/20 dark:from-brand-500/10 dark:to-transparent'"
        (click)="selectDrill('volume', null, 'New inquiries in period')"
      >
        <span class="absolute inset-y-0 left-0 w-1 bg-brand-500" aria-hidden="true"></span>
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-300">New inquiries</span>
          <span class="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-100 text-brand-600 dark:bg-brand-500/20 dark:text-brand-300">
            <lucide-icon [name]="Users" class="h-4 w-4"></lucide-icon>
          </span>
        </div>
        <div class="mt-2 flex items-end gap-2">
          <span class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ metrics.volume.new_contacts | number }}</span>
          <span class="mb-1 inline-flex items-center gap-0.5 text-xs font-semibold" [ngClass]="trendClass(metrics.volume.trend)">
            <lucide-icon [name]="trendIcon(metrics.volume.trend)" class="h-3 w-3"></lucide-icon>{{ trendLabel(metrics.volume.trend) }}
          </span>
        </div>
        <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">new contacts · vs previous period</div>
      </button>

      <!-- High value -->
      <button
        type="button"
        class="group relative overflow-hidden rounded-2xl border p-4 text-left transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
        [ngClass]="isActiveDrill('high-value')
          ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-300 dark:border-amber-500/60 dark:bg-amber-500/15 dark:ring-amber-500/40'
          : 'border-amber-200/70 bg-gradient-to-br from-amber-50 to-white hover:border-amber-300 dark:border-amber-500/20 dark:from-amber-500/10 dark:to-transparent'"
        (click)="selectDrill('high-value', null, 'High-value contacts')"
      >
        <span class="absolute inset-y-0 left-0 w-1 bg-amber-500" aria-hidden="true"></span>
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">High value</span>
          <span class="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300">
            <lucide-icon [name]="PoundSterling" class="h-4 w-4"></lucide-icon>
          </span>
        </div>
        <div class="mt-2 flex items-end gap-2">
          <span class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ metrics.value.high_value_contacts | number }}</span>
          <span class="mb-1 inline-flex items-center gap-0.5 text-xs font-semibold" [ngClass]="trendClass(metrics.value.high_value_trend)">
            <lucide-icon [name]="trendIcon(metrics.value.high_value_trend)" class="h-3 w-3"></lucide-icon>{{ trendLabel(metrics.value.high_value_trend) }}
          </span>
        </div>
        <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">≥ {{ metrics.value.high_value_threshold | currency:'GBP':'symbol':'1.0-0' }} likely value</div>
      </button>

      <!-- Average value (non-clickable) -->
      <div class="relative overflow-hidden rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-white p-4 dark:border-emerald-500/20 dark:from-emerald-500/10 dark:to-transparent">
        <span class="absolute inset-y-0 left-0 w-1 bg-emerald-500" aria-hidden="true"></span>
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Avg value / contact</span>
          <span class="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300">
            <lucide-icon [name]="TrendingUp" class="h-4 w-4"></lucide-icon>
          </span>
        </div>
        <div class="mt-2 flex items-end gap-2">
          <span class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {{ metrics.value.average_value !== null ? (metrics.value.average_value | currency:'GBP':'symbol':'1.0-0') : '—' }}
          </span>
          <span class="mb-1 inline-flex items-center gap-0.5 text-xs font-semibold" [ngClass]="trendClass(metrics.value.average_value_trend)">
            <lucide-icon [name]="trendIcon(metrics.value.average_value_trend)" class="h-3 w-3"></lucide-icon>{{ trendLabel(metrics.value.average_value_trend) }}
          </span>
        </div>
        <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ metrics.value.contacts_with_value | number }} pipeline contacts with a value</div>
      </div>

      <!-- Going cold -->
      <button
        type="button"
        class="group relative overflow-hidden rounded-2xl border p-4 text-left transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
        [ngClass]="isActiveDrill('going-cold')
          ? 'border-sky-400 bg-sky-50 ring-2 ring-sky-300 dark:border-sky-500/60 dark:bg-sky-500/15 dark:ring-sky-500/40'
          : 'border-sky-200/70 bg-gradient-to-br from-sky-50 to-white hover:border-sky-300 dark:border-sky-500/20 dark:from-sky-500/10 dark:to-transparent'"
        (click)="selectDrill('going-cold', null, 'Going cold')"
      >
        <span class="absolute inset-y-0 left-0 w-1 bg-sky-500" aria-hidden="true"></span>
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-300">Going cold</span>
          <span class="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 text-sky-600 dark:bg-sky-500/20 dark:text-sky-300">
            <lucide-icon [name]="Snowflake" class="h-4 w-4"></lucide-icon>
          </span>
        </div>
        <div class="mt-2 flex items-end gap-2">
          <span class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ metrics.going_cold.contacts | number }}</span>
          <span class="mb-1 inline-flex items-center gap-0.5 text-xs font-semibold" [ngClass]="trendClass(metrics.going_cold.trend, false)">
            <lucide-icon [name]="trendIcon(metrics.going_cold.trend)" class="h-3 w-3"></lucide-icon>{{ trendLabel(metrics.going_cold.trend) }}
          </span>
        </div>
        <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">no activity for {{ metrics.going_cold.going_cold_days }}+ days</div>
      </button>
    </div>

    <!-- Funnel — horizontal bars sized to each stage so the shape reads as a
         funnel rather than five identical boxes. Each bar is a drill target. -->
    <div class="mt-6 rounded-xl border border-gray-200 p-4 dark:border-gray-800">
      <h3 class="mb-3 text-sm font-semibold text-gray-800 dark:text-white/90">Current funnel</h3>
      <div class="space-y-1.5">
        @for (stage of metrics.funnel; track stage.stage) {
          <button
            type="button"
            class="group flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 dark:hover:bg-white/[0.04]"
            [ngClass]="isActiveDrill('funnel', stage.stage) ? 'bg-gray-50 ring-1 ring-brand-300 dark:bg-white/[0.04] dark:ring-brand-500/40' : ''"
            (click)="drillFunnel(stage)"
          >
            <span class="w-32 shrink-0 truncate text-xs font-medium" [ngClass]="isActiveDrill('funnel', stage.stage) ? 'text-brand-600 dark:text-brand-400' : 'text-gray-600 dark:text-gray-300'">{{ stage.label }}</span>
            <span class="relative h-7 flex-1 overflow-hidden rounded-md bg-gray-100 dark:bg-white/[0.06]">
              <span
                class="absolute inset-y-0 left-0 rounded-md transition-all"
                [ngClass]="funnelBarClass(stage.stage)"
                [style.width.%]="funnelBarWidth(stage)"
              ></span>
            </span>
            <span class="w-10 shrink-0 text-right text-sm font-bold tabular-nums text-gray-900 dark:text-white">{{ stage.contacts | number }}</span>
          </button>
        }
      </div>
    </div>

    <!-- Conversion -->
    <div class="mt-6">
      <div class="mb-2 flex items-center justify-between gap-2">
        <h3 class="text-sm font-semibold text-gray-800 dark:text-white/90">Conversion (in period)</h3>
        <div class="flex gap-1.5" role="group" aria-label="Break conversion down by">
          <button
            type="button"
            class="rounded-full border px-3 py-1 text-xs font-medium"
            [ngClass]="groupBy === 'venue' ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-500/40 dark:bg-brand-500/15 dark:text-brand-300' : 'border-gray-300 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'"
            (click)="setGroupBy('venue')"
          >By venue</button>
          <button
            type="button"
            class="rounded-full border px-3 py-1 text-xs font-medium"
            [ngClass]="groupBy === 'salesperson' ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-500/40 dark:bg-brand-500/15 dark:text-brand-300' : 'border-gray-300 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'"
            (click)="setGroupBy('salesperson')"
          >By salesperson</button>
        </div>
      </div>

      <div class="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
        <!-- Overall conversion: one horizontal bar per stage-to-stage step. -->
        <div class="space-y-2.5">
          @for (step of conversionSteps(metrics.conversion.overall); track step.label) {
            <div class="flex items-center gap-3">
              <span class="w-36 shrink-0 text-xs font-medium text-gray-600 dark:text-gray-300">{{ step.label }}</span>
              <span class="relative h-4 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.06]">
                <span class="absolute inset-y-0 left-0 rounded-full transition-all" [ngClass]="step.barClass" [style.width.%]="step.rate ?? 0"></span>
              </span>
              <span class="w-12 shrink-0 text-right text-sm font-semibold tabular-nums text-gray-800 dark:text-white/90">{{ step.rate !== null ? step.rate + '%' : '—' }}</span>
            </div>
          }
        </div>

        <!-- Per-group breakdown: the same three steps as compact mini-bars. -->
        <div class="mt-4 border-t border-gray-100 pt-4 dark:border-gray-800">
          <div class="mb-3 flex items-center gap-4 text-[11px] font-medium text-gray-500 dark:text-gray-400">
            <span class="uppercase tracking-wide">By {{ groupBy === 'venue' ? 'venue' : 'salesperson' }}</span>
            <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-full bg-blue-500"></span>Quote→Viewing</span>
            <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-full bg-violet-500"></span>Viewing→Provisional</span>
            <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-full bg-emerald-500"></span>Provisional→Confirmed</span>
          </div>
          <div class="space-y-3">
            @for (row of metrics.conversion.groups; track row.label) {
              <div>
                <div class="mb-1 truncate text-xs font-medium text-gray-700 dark:text-gray-200">{{ row.label }}</div>
                <div class="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                  @for (step of conversionSteps(row); track step.label) {
                    <span class="flex items-center gap-2">
                      <span class="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.06]">
                        <span class="absolute inset-y-0 left-0 rounded-full" [ngClass]="step.barClass" [style.width.%]="step.rate ?? 0"></span>
                      </span>
                      <span class="w-9 shrink-0 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">{{ step.rate !== null ? step.rate + '%' : '—' }}</span>
                    </span>
                  }
                </div>
              </div>
            } @empty {
              <div class="py-2 text-center text-sm text-gray-500 dark:text-gray-400">No stage movement in this period.</div>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Drill-down worklist -->
  <div class="mt-6">
    <div class="mb-3 flex items-center justify-between gap-2">
      <h3 class="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-800 dark:text-white/90">
        <span>Worklist</span>
        <!-- Active drill chip — has an × to return to the full pipeline view -->
        <span class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
          [ngClass]="isActiveDrill('pipeline')
            ? 'bg-gray-100 text-gray-600 dark:bg-white/[0.06] dark:text-gray-300'
            : 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'">
          {{ activeDrill.title }}
          @if (!isActiveDrill('pipeline')) {
            <button
              type="button"
              class="-mr-0.5 ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-brand-200 dark:hover:bg-brand-500/30"
              aria-label="Clear filter"
              (click)="clearDrill()"
            >
              <lucide-icon [name]="X" class="h-2.5 w-2.5"></lucide-icon>
            </button>
          }
        </span>
        @if (contactsMeta) {
          <span class="text-xs font-normal text-gray-500 dark:text-gray-400">{{ contactsMeta.total | number }} contacts</span>
        }
      </h3>
    </div>

    <div class="relative overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      @if (loadingContacts) {
        <div class="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-black/40">
          <div class="animate-spin h-5 w-5 rounded-full border-2 border-brand-500 border-t-transparent"></div>
        </div>
      }
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-white/[0.03] dark:text-gray-400">
          <tr>
            <th class="px-3 py-2 font-medium">Contact</th>
            <th class="px-3 py-2 font-medium">Stage</th>
            <th class="px-3 py-2 text-right font-medium">Quotes</th>
            <th class="px-3 py-2 text-right font-medium">Events</th>
            <th class="px-3 py-2 text-right font-medium">Likely value</th>
            <th class="px-3 py-2 font-medium">Last activity</th>
            <th class="px-3 py-2 font-medium">Salesperson</th>
            <th class="px-3 py-2 font-medium">Venue</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
          @for (row of contacts; track row.contact_id) {
            <tr
              class="cursor-pointer text-gray-700 transition-colors hover:bg-brand-50/40 dark:text-white/80 dark:hover:bg-white/[0.06]"
              [ngClass]="$odd ? 'bg-gray-50/60 dark:bg-white/[0.02]' : 'bg-white dark:bg-transparent'"
              (click)="navigateToContact(row.contact_id)"
            >
              <td class="px-3 py-2.5">
                <div class="font-medium text-gray-900 hover:text-brand-600 dark:text-white/90 dark:hover:text-brand-400">{{ row.name }}</div>
                @if (row.email) {
                  <div class="text-xs text-gray-400 dark:text-gray-500">{{ row.email }}</div>
                }
              </td>
              <td class="px-3 py-2.5">
                <span class="inline-flex rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="stageBadgeClass(row.stage)">{{ row.stage_label }}</span>
              </td>
              <td class="px-3 py-2.5 text-right tabular-nums">{{ row.quote_count | number }}</td>
              <td class="px-3 py-2.5 text-right tabular-nums">{{ row.event_count | number }}</td>
              <td class="px-3 py-2.5 text-right font-medium tabular-nums text-gray-800 dark:text-white/80">{{ row.likely_value !== null ? (row.likely_value | currency:'GBP':'symbol':'1.0-0') : '—' }}</td>
              <td class="px-3 py-2.5 text-gray-500 dark:text-gray-400">{{ row.last_activity_at ? (row.last_activity_at | date:'d MMM y') : '—' }}</td>
              <td class="px-3 py-2.5 text-gray-600 dark:text-gray-300">{{ row.salesperson ? row.salesperson.name : '—' }}</td>
              <td class="px-3 py-2.5 text-gray-600 dark:text-gray-300">
                @if (row.venues.length) {
                  {{ row.venues[0].name }}@if (row.venues.length > 1) { <span class="ml-1 text-xs text-gray-400">+{{ row.venues.length - 1 }}</span> }
                } @else {
                  —
                }
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="8" class="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">No contacts match this view.</td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    @if (contactsMeta && totalPages() > 1) {
      <div class="mt-3 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
        <span>Page {{ contactsMeta.page }} of {{ totalPages() }}</span>
        <div class="flex gap-2">
          <button type="button" class="rounded-lg border border-gray-200 px-3 py-1 disabled:opacity-40 dark:border-gray-700" [disabled]="page <= 1" (click)="goToPage(page - 1)">Previous</button>
          <button type="button" class="rounded-lg border border-gray-200 px-3 py-1 disabled:opacity-40 dark:border-gray-700" [disabled]="page >= totalPages()" (click)="goToPage(page + 1)">Next</button>
        </div>
      </div>
    }
  </div>
</div>
